/**
 * Schedule for the "Built For Every Renter" scene: four renter profiles take
 * turns on one landlord-view card.
 *
 * Each turn is a cycle. The outgoing texts fade (`swap`), the new profile
 * fills in (`fill`), then the card plays its strike, rows and stamp (`on`).
 * After RENTER_ADVANCE_MS the next profile starts, unless the viewer is
 * hovering the tile or has a chip focused, in which case the card holds and
 * moves on as soon as they leave. A chip click jumps to that profile and
 * restarts the clock. Everything here is pure and unit tested; the scene
 * component drives it from the animation frame.
 */

export interface RenterProfile {
	initials: string
	name: string
	/** The credit word the card strikes through. */
	score: string
	/** The chip label, repeated as the green tag on the card. */
	tag: string
}

/** Demo renters for the card. Tags double as the chip labels. */
export const RENTER_PROFILES: readonly RenterProfile[] = [
	{
		initials: 'MT',
		name: 'Marcus T.',
		score: 'Below Average',
		tag: 'Low Credit',
	},
	{
		initials: 'JL',
		name: 'Jordan L.',
		score: 'Thin File',
		tag: 'First-Time Renter',
	},
	{
		initials: 'SD',
		name: 'Sarah, Daniel + Priya',
		score: 'Mixed',
		tag: 'Roommates',
	},
	{
		initials: 'AO',
		name: 'Amara O.',
		score: 'No History',
		tag: 'New to Canada',
	},
]

/** The fixed words on the card. */
export const RENTER_SCENE_COPY = {
	tablist: 'Renter profiles',
	credit: 'Credit',
	rows: ['ID', 'Income', 'Rent History'],
	stamp: 'Approved',
} as const

/** The outgoing texts fade for this long before the new profile fills in. */
export const RENTER_SWAP_MS = 300
/** The strike, rows and stamp start playing this far into a cycle. */
export const RENTER_REVEAL_MS = 600
/** A profile holds the card this long before the next one takes over. */
export const RENTER_ADVANCE_MS = 5600

export type RenterPhase = 'idle' | 'swap' | 'fill' | 'on'

export interface RenterCycle {
	/** The profile the chips point at: the one being revealed. */
	index: number
	/** The profile whose texts fade out during the swap. */
	from: number
	/** Scene-clock time the cycle began. Null until the scene starts. */
	startedAt: number | null
}

/** Before the tile scrolls in, and again after it scrolls out. */
export const IDLE_RENTER_CYCLE: RenterCycle = {
	index: 0,
	from: 0,
	startedAt: null,
}

export function wrapRenterIndex(
	index: number,
	count = RENTER_PROFILES.length,
): number {
	if (count <= 0) return 0
	return ((index % count) + count) % count
}

/** Which part of a cycle plays `elapsedMs` after it started. */
export function renterPhaseAt(elapsedMs: number): Exclude<RenterPhase, 'idle'> {
	if (elapsedMs < RENTER_SWAP_MS) return 'swap'
	if (elapsedMs < RENTER_REVEAL_MS) return 'fill'
	return 'on'
}

/** The first cycle after the tile scrolls in. */
export function startRenterCycle(now: number, index = 0): RenterCycle {
	const start = wrapRenterIndex(index)
	return { index: start, from: start, startedAt: now }
}

/**
 * The cycle one frame later. Returns the same object (so callers can skip a
 * render) until the hold is over; while `paused` the card keeps holding and
 * advances on the first frame after the pause ends.
 */
export function advanceRenterCycle(
	cycle: RenterCycle,
	now: number,
	paused: boolean,
	count = RENTER_PROFILES.length,
): RenterCycle {
	if (cycle.startedAt === null || paused) return cycle
	if (now - cycle.startedAt < RENTER_ADVANCE_MS) return cycle
	return {
		index: wrapRenterIndex(cycle.index + 1, count),
		from: cycle.index,
		startedAt: now,
	}
}

/**
 * A chip click: the card swaps from whatever it is showing (`shownIndex`,
 * which may be the outgoing profile mid-swap) to `target` and the clock
 * restarts. An idle scene just changes which profile it will start with.
 */
export function jumpToRenterProfile(
	cycle: RenterCycle,
	target: number,
	now: number,
	shownIndex: number,
	count = RENTER_PROFILES.length,
): RenterCycle {
	return {
		index: wrapRenterIndex(target, count),
		from: shownIndex,
		startedAt: cycle.startedAt === null ? null : now,
	}
}

export interface RenterSceneView {
	/** The profile whose texts are on the card right now. */
	shownIndex: number
	/** True while the outgoing texts fade. Same-profile replays never fade. */
	textsHidden: boolean
	/** True once the strike, rows and stamp should play. */
	on: boolean
}

/** What the card draws for a cycle in a phase. */
export function renterSceneView(
	cycle: RenterCycle,
	phase: RenterPhase,
): RenterSceneView {
	const swapping = phase === 'swap'
	return {
		shownIndex: swapping ? cycle.from : cycle.index,
		textsHidden: swapping && cycle.from !== cycle.index,
		on: phase === 'on',
	}
}

/**
 * Roving-tabindex movement for the chip tablist: arrows wrap, Home and End
 * jump. Null for any other key so the event keeps its default.
 */
export function renterTabForKey(
	key: string,
	index: number,
	count = RENTER_PROFILES.length,
): number | null {
	switch (key) {
		case 'ArrowRight':
		case 'ArrowDown':
			return wrapRenterIndex(index + 1, count)
		case 'ArrowLeft':
		case 'ArrowUp':
			return wrapRenterIndex(index - 1, count)
		case 'Home':
			return 0
		case 'End':
			return Math.max(0, count - 1)
		default:
			return null
	}
}
